import React from "react";

import type {
  AudienceSelectionRef,
  StudyDesign,
  SurveyJson,
} from "@digital-twins/shared-types";

import { QuestionnaireBuilderChat } from "@/features/studies/questionnaire/components/QuestionnaireBuilderChat";
import type { SelectedPopulation } from "@/features/studies/questionnaire/utils/draftAudience";
import type { StudyType } from "../types";
import type { Storage } from "../state/creationState";

interface ChatStepWrapperProps {
  /** Flow-header chips (goal) rendered in the builder's top bar. */
  headerSlot?: React.ReactNode;
  /** Lands a builder-side language change back into creation state. */
  onContentLanguageChange?: (lang: string) => void;
  studyType: StudyType;
  /** The picked audience, with its filter and segments (written to the draft). */
  selectedPopulation: SelectedPopulation | null;
  selectedStorageId: string;
  storages: Storage[];
  questionnaireDraftId?: string | null;
  onQuestionnaireDraftIdChange?: (draftId: string) => void;
  onSurveyReady: (
    surveyJson: SurveyJson,
    revision: number,
    studyTitle?: string | null,
    audience?: AudienceSelectionRef | null,
  ) => Promise<boolean>;
  returnToStep?: number | null;
  onReturnToReview?: () => void;
  onEditAudience?: () => void;
  initialMessage?: string | null;
  contentLanguage?: string;
  /** §0 goal id — seeds the chat's authored opening chain at creation. */
  goalId?: string | null;
  /** Hydrates creation state from the loaded draft's StudySpec for ReviewStep pricing. */
  onStudyDesign?: (design: StudyDesign | null) => void;
  /** The builder's "Save and continue", for the flow's own controls that leave its draft. */
  saveFirstRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
}

export function ChatStepWrapper({
  headerSlot,
  onContentLanguageChange,
  studyType,
  selectedPopulation,
  selectedStorageId,
  storages,
  questionnaireDraftId,
  onQuestionnaireDraftIdChange,
  onSurveyReady,
  returnToStep,
  onReturnToReview,
  onEditAudience,
  initialMessage,
  contentLanguage,
  goalId,
  onStudyDesign,
  saveFirstRef,
}: ChatStepWrapperProps) {
  return (
    <div key="questionnaire-builder" className="flex-1 min-h-0 flex flex-col">
      <QuestionnaireBuilderChat
        headerSlot={headerSlot}
        onLanguageChange={onContentLanguageChange}
        studyType={studyType}
        selectedPopulation={selectedPopulation}
        selectedStorage={
          selectedStorageId
            ? {
                id: selectedStorageId,
                name:
                  storages.find((s) => s.id === selectedStorageId)?.name ||
                  selectedStorageId,
              }
            : null
        }
        onSurveyReady={onSurveyReady}
        onEditAudience={onEditAudience}
        resumeSessionId={questionnaireDraftId}
        onSessionReady={onQuestionnaireDraftIdChange}
        initialMessage={initialMessage}
        language={contentLanguage}
        goalId={goalId}
        onStudyDesign={onStudyDesign}
        returnToStep={returnToStep}
        onReturnToReview={onReturnToReview}
        saveFirstRef={saveFirstRef}
      />
    </div>
  );
}
